'use client';

import { Loader2 } from 'lucide-react';
import * as React from 'react';
import { type Control, type FieldValues, useFormState } from 'react-hook-form';

import { Button, type ButtonProps } from '@/app/_components/ui/button';
import { cn } from '@/lib/utils';

// Submit button that subscribes to `formState.isValid` via
// `useFormState({ control })` rather than reading it from `form.formState`
// directly. This isolates the subscription inside this leaf component, so
// react-hook-form's underlying setState when validity changes doesn't
// propagate up to a parent that contains <Controller>s. Without this
// isolation React 19.2 logs the warning:
//   "Cannot update a component (X) while rendering a different component (Controller)"
// and, depending on timing, leaves the submit button stuck disabled even
// after the form becomes valid.
//
// While we're at it, the component shows a centered spinner when `pending`
// is true, keeping the label in the layout flow (visibility:hidden) so the
// button width does not jump between the idle and loading states.
//
// The component intentionally mirrors `<Button>`'s API so callers can drop
// it in by replacing `<Button type="submit" disabled={pending || !form.formState.isValid}>`
// with `<FormSubmitButton control={form.control} pending={pending}>`.
type FormSubmitButtonProps<T extends FieldValues> = Omit<
  ButtonProps,
  'disabled'
> & {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<T, any, any>;
  /** Combined with `!isValid` to compute the final `disabled` state.
   *  Also drives the spinner overlay. */
  pending?: boolean;
  /** Extra disable condition (e.g. read-only mode) combined with the above. */
  extraDisabled?: boolean;
};

function FormSubmitButtonInner<T extends FieldValues>(
  {
    control,
    pending = false,
    extraDisabled = false,
    type = 'submit',
    className,
    children,
    ...buttonProps
  }: FormSubmitButtonProps<T>,
  ref: React.ForwardedRef<HTMLButtonElement>,
) {
  const { isValid } = useFormState({ control });
  return (
    <Button
      {...buttonProps}
      ref={ref}
      type={type}
      disabled={extraDisabled || pending || !isValid}
      className={cn('relative', className)}
    >
      {pending ? (
        // While pending, hide the original children (kept in flow with
        // visibility:hidden so the button width doesn't change) and overlay
        // a centered spinner. The wrapping <span> would otherwise collapse
        // the parent's flex layout to a single child, so we only inject it
        // when actually needed.
        <>
          <span className="invisible">{children}</span>
          <Loader2 className="absolute inset-0 m-auto h-4 w-4 animate-spin" />
        </>
      ) : (
        children
      )}
    </Button>
  );
}

// Cast required because forwardRef doesn't preserve generic parameters.
export const FormSubmitButton = React.forwardRef(FormSubmitButtonInner) as <
  T extends FieldValues,
>(
  props: FormSubmitButtonProps<T> & {
    ref?: React.ForwardedRef<HTMLButtonElement>;
  },
) => React.ReactElement;
